import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Typography,
    Switch,
    FormControlLabel,
    Chip,
    Divider
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { Repeat, EventRepeat } from '@mui/icons-material';
import ClientAutocomplete from './ClientAutocomplete';

export interface ReservationFormData {
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    boardId: string;
    startDate: Date;
    endDate: Date;
    startTime: Date;
    endTime: Date;
    notes: string;
    status: 'pending' | 'confirmed' | 'cancelled';
    paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
    rescheduleDate?: Date | null;
    isRecurring?: boolean;
    recurringPattern?: 'daily' | 'weekly';
    recurringEndDate?: Date;
    recurringDays?: number[]; // 0=Sunday, 1=Monday, etc.
}

interface Board {
    id: string;
    name: string;
    type: string;
}

interface Reservation {
    id: string;
    startDate: string;
    endDate: string;
    status: string;
    board: {
        id: string;
    };
}

interface ReservationFormProps {
    initialData?: Partial<ReservationFormData>;
    onSubmit: (data: ReservationFormData) => Promise<void>;
    onCancel: () => void;
    boards?: Board[];
    existingReservations?: Reservation[];
    isEditMode?: boolean;
    loading?: boolean;
    error?: string;
}

const ReservationForm: React.FC<ReservationFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    boards = [],
    existingReservations = [],
    isEditMode = false,
    loading = false,
    error: externalError
}) => {
    const [formData, setFormData] = useState<ReservationFormData>({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        boardId: boards.length > 0 ? boards[0].id : '',
        startDate: new Date(),
        endDate: new Date(),
        startTime: new Date(),
        endTime: new Date(),
        notes: '',
        status: 'confirmed',
        paymentStatus: 'pending',
        rescheduleDate: null,
        isRecurring: false,
        recurringPattern: 'weekly',
        recurringEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1)), // Default 1 month
        recurringDays: [],
        ...initialData
    });

    // Set default recurring day based on start date
    useEffect(() => {
        if (formData.isRecurring && formData.recurringDays && formData.recurringDays.length === 0) {
            setFormData(prev => ({
                ...prev,
                recurringDays: [prev.startDate.getDay()]
            }));
        }
    }, [formData.isRecurring, formData.startDate]);

    const [conflictWarning, setConflictWarning] = useState('');
    const [internalError, setInternalError] = useState('');

    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({ ...prev, ...initialData }));
        }
    }, [initialData]);

    useEffect(() => {
        validateConflict();
    }, [formData.startDate, formData.endDate, formData.startTime, formData.endTime, formData.boardId]);

    const validateConflict = () => {
        if (!existingReservations || !formData.boardId) return;

        const start = new Date(formData.startDate);
        start.setHours(formData.startTime.getHours(), formData.startTime.getMinutes());

        const end = new Date(formData.endDate);
        end.setHours(formData.endTime.getHours(), formData.endTime.getMinutes());

        const hasConflict = existingReservations.some(res => {
            if (res.status === 'cancelled') return false;
            // Strict ID check would be better if we had reservation ID in form data
            if (res.board.id !== formData.boardId) return false;

            const resStart = new Date(res.startDate);
            const resEnd = new Date(res.endDate);

            return start < resEnd && end > resStart;
        });

        if (hasConflict) {
            setConflictWarning('⚠️ Conflicto: Ya existe una reserva en este horario.');
        } else {
            setConflictWarning('');
        }
    };

    const handleSubmit = () => {
        // Strict Validation
        if (!formData.customerName) {
            setInternalError('Por favor ingresa un nombre para el cliente.');
            return;
        }
        if (!formData.customerEmail) {
            setInternalError('El email es obligatorio para crear una reserva.');
            return;
        }
        if (!formData.boardId) {
            setInternalError('Por favor selecciona una agenda/tablero.');
            return;
        }

        const start = new Date(formData.startDate);
        start.setHours(formData.startTime.getHours(), formData.startTime.getMinutes());

        const end = new Date(formData.endDate);
        end.setHours(formData.endTime.getHours(), formData.endTime.getMinutes());

        if (start >= end) {
            setInternalError('La hora de fin debe ser posterior a la de inicio.');
            return;
        }

        if (formData.isRecurring) {
            if (!formData.recurringEndDate || formData.recurringEndDate <= formData.startDate) {
                setInternalError('La fecha de fin de repetición debe ser posterior a la fecha de inicio.');
                return;
            }
        }

        setInternalError('');
        
        // Pass the calculated start/end dates to onSubmit
        onSubmit({
            ...formData,
            startDate: start,
            endDate: end
        });
    };

    const toggleDay = (dayIndex: number) => {
        if (!formData.recurringDays) return;
        const newDays = formData.recurringDays.includes(dayIndex)
            ? formData.recurringDays.filter(d => d !== dayIndex)
            : [...formData.recurringDays, dayIndex];
        setFormData({ ...formData, recurringDays: newDays });
    };

    const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(internalError || externalError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>{internalError || externalError}</Alert>
                )}
                {conflictWarning && <Alert severity="warning" sx={{ mb: 2 }}>{conflictWarning}</Alert>}

                {boards.length > 0 && (
                    <FormControl fullWidth size="small">
                        <InputLabel>Agenda / Tablero *</InputLabel>
                        <Select
                            value={formData.boardId}
                            label="Agenda / Tablero *"
                            onChange={(e) => setFormData({ ...formData, boardId: e.target.value })}
                        >
                            {boards.map(board => (
                                <MenuItem key={board.id} value={board.id}>
                                    {board.name} ({board.type})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <ClientAutocomplete
                    value={formData.customerName ? { name: formData.customerName, email: formData.customerEmail, phone: formData.customerPhone, id: '' } : null}
                    onChange={(newValue) => {
                        if (typeof newValue === 'string') {
                            setFormData({ ...formData, customerName: newValue });
                        } else if (newValue === null) {
                            setFormData({ ...formData, customerName: '', customerEmail: '', customerPhone: '' });
                        }
                    }}
                    onClientSelect={(client) => {
                        setFormData({
                            ...formData,
                            customerName: client.name,
                            customerEmail: client.email,
                            customerPhone: client.phone || ''
                        });
                    }}
                    error={!!internalError && !formData.customerName}
                    label="Nombre del Cliente *"
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Email *"
                        fullWidth
                        size="small"
                        value={formData.customerEmail}
                        onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                        error={!!internalError && !formData.customerEmail}
                    />
                    <TextField
                        label="Teléfono"
                        fullWidth
                        size="small"
                        value={formData.customerPhone}
                        onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DatePicker
                        label="Fecha Inicio"
                        value={formData.startDate}
                        onChange={(newValue) => setFormData({ ...formData, startDate: newValue || new Date() })}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    <TimePicker
                        label="Hora Inicio"
                        value={formData.startTime}
                        onChange={(newValue) => setFormData({ ...formData, startTime: newValue || new Date() })}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DatePicker
                        label="Fecha Fin (Día)"
                        value={formData.endDate}
                        onChange={(newValue) => setFormData({ ...formData, endDate: newValue || new Date() })}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    <TimePicker
                        label="Hora Fin"
                        value={formData.endTime}
                        onChange={(newValue) => setFormData({ ...formData, endTime: newValue || new Date() })}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                </Box>

                {/* Recurring Section */}
                {!isEditMode && (
                    <Box sx={{ bgcolor: 'secondary.50', p: 2, borderRadius: 2, border: '1px dashed #BE93D4' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isRecurring}
                                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                    color="secondary"
                                />
                            }
                            label={<Box display="flex" alignItems="center" gap={1}><Repeat /> Repetir Reserva</Box>}
                        />

                        {formData.isRecurring && (
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Frecuencia</InputLabel>
                                        <Select
                                            value={formData.recurringPattern}
                                            label="Frecuencia"
                                            onChange={(e) => setFormData({ ...formData, recurringPattern: e.target.value as any })}
                                        >
                                            <MenuItem value="weekly">Semanal</MenuItem>
                                            <MenuItem value="daily">Diaria</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <DatePicker
                                        label="Repetir hasta"
                                        value={formData.recurringEndDate}
                                        onChange={(newValue) => setFormData({ ...formData, recurringEndDate: newValue || new Date() })}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </Box>

                                {formData.recurringPattern === 'weekly' && (
                                    <Box>
                                        <Typography variant="caption" display="block" mb={1}>Días de la semana:</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {daysOfWeek.map((day, index) => (
                                                <Chip
                                                    key={day}
                                                    label={day}
                                                    size="small"
                                                    onClick={() => toggleDay(index)}
                                                    color={formData.recurringDays?.includes(index) ? "secondary" : "default"}
                                                    variant={formData.recurringDays?.includes(index) ? "filled" : "outlined"}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                <Alert severity="info" icon={<EventRepeat />}>
                                    Se crearán reservas individules en el calendario.
                                </Alert>
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Estado</InputLabel>
                        <Select
                            value={formData.status}
                            label="Estado"
                            onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        >
                            <MenuItem value="confirmed">Confirmada</MenuItem>
                            <MenuItem value="pending">Pendiente</MenuItem>
                            <MenuItem value="cancelled">Cancelada</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Pago</InputLabel>
                        <Select
                            value={formData.paymentStatus}
                            label="Pago"
                            onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value as any })}
                        >
                            <MenuItem value="pending">Pendiente</MenuItem>
                            <MenuItem value="partial">Parcial</MenuItem>
                            <MenuItem value="paid">Pagado</MenuItem>
                            <MenuItem value="refunded">Reembolsado</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {isEditMode && formData.status === 'cancelled' && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error" gutterBottom>
                            Opciones de Cancelación
                        </Typography>
                        <DatePicker
                            label="Sugerir reprogramación a partir de..."
                            value={formData.rescheduleDate}
                            onChange={(newValue) => setFormData({ ...formData, rescheduleDate: newValue })}
                            slotProps={{ textField: { fullWidth: true, helperText: 'Se enviará un enlace para reprogramar desde esta fecha' } }}
                        />
                    </Box>
                )}

                <TextField
                    label="Notas"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button onClick={onCancel} disabled={loading}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {isEditMode ? 'Guardar Cambios' : (formData.isRecurring ? 'Crear Serie' : 'Crear Reserva')}
                    </Button>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default ReservationForm;
